import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { AssimagraMetrics } from "../../types/assimagra/assimagra-metrics.types";
import { useLanguage } from "../../contexts/LanguageContext";

interface TransformationProductsPieChartProps {
  metrics: AssimagraMetrics | null;
}

export const TransformationProductsPieChart = ({ metrics }: TransformationProductsPieChartProps) => {
  const { t } = useLanguage();

  // Provide default values if metrics is null
  const safeMetrics = metrics || {
    transformation_products: {},
  };

  // Colors for different product types
  const colors = [
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
    "#84CC16",
    "#F97316",
    "#EC4899",
    "#6B7280",
  ];

  // Prepare transformation products data
  const transformationData = Object.entries(safeMetrics.transformation_products)
    .filter(([_, value]) => value > 0)
    .map(([key, value], index) => ({
      name: key,
      value,
      color: colors[index % colors.length],
    }));

  const totalValue = transformationData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = totalValue > 0 ? ((data.value / totalValue) * 100).toFixed(1) : "0";
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.payload.name}</p>
          <p className="text-sm text-gray-600">
            {t("assimagraDashboard.charts.productTypes.tooltip.count")}:{" "}
            <span className="font-medium">{data.value}</span>
          </p>
          <p className="text-sm text-gray-600">
            {t("assimagraDashboard.charts.productTypes.tooltip.percentage")}:{" "}
            <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap justify-center gap-2 mt-2">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-sm text-gray-600">
              {entry.value} ({entry.payload.value})
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
  <Card className="flex flex-col h-full">
    <CardHeader>
      <CardTitle className="text-base font-semibold text-gray-900">
        {t("assimagraDashboard.charts.productTypes.transformation.title")}
      </CardTitle>
      <p className="text-sm text-gray-500">
        {t("assimagraDashboard.charts.productTypes.total", {
          total: totalValue,
        })}
      </p>
    </CardHeader>
    <CardContent>
      {totalValue > 0 ? (
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={transformationData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={100}
                paddingAngle={1}
                dataKey="value"
              >
                {transformationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-72 text-gray-500">
          <div className="text-center">
            <p className="text-base mb-1">
              {t("assimagraDashboard.charts.stoneTypes.noData")}
            </p>
            <p className="text-sm">
              {t("assimagraDashboard.charts.productTypes.transformation.noData")}
            </p>
          </div>
        </div>
      )}
    </CardContent>
    {totalValue > 0 && (
      <CardFooter className="flex justify-center">
        <CustomLegend
          payload={transformationData.map((d) => ({
            value: d.name,
            color: d.color,
            payload: { value: d.value },
          }))}
        />
      </CardFooter>
    )}
  </Card>
);
};
